import type { AxiosInstance, AxiosRequestConfig } from 'axios'

import {
  OVERVIEW_VIEW_TABLE_ID,
  type OverviewViewRow,
  type SavedOverviewViewDto,
} from '../models/budget.types'

import { appHttp } from '@/shared/http'

/** Errors belong to the caller, not the global toast (contract 03 §15). */
const FEATURE_OWNED_ERRORS: AxiosRequestConfig = {
  suppressGlobalErrorHandler: true,
  meta: { suppressGlobalErrorHandler: true },
}

/**
 * Performance Overview saved view — contract 27 §6.1 #1 (v3 `PerformanceOverviewArea.vue:244`).
 *
 * ⚠️ `upsert_table_view_config` upserts the WHOLE row and blanks the fields a caller omits — a
 * verified backend behaviour, live in v3, that wipes every co-writer of the same `table_id`.
 * The write therefore REQUIRES the current row (read-merge-write): the caller fetches it through
 * module 08's read surface and this method echoes every foreign field back verbatim, replacing
 * only the `mediaColumns` blob this feature owns. `current: null` means the row does not exist
 * yet, and the foreign fields are seeded exactly as v3 seeded them (empty).
 *
 * The READ side (`GET workspaces/{ws}/get_table_view_configs`) is deliberately NOT here: it is
 * module 08's, reached through `useReportViewConfigs`. This feature has one saved-view endpoint.
 */
export class SavedViewApi {
  constructor(private readonly http: AxiosInstance = appHttp) {}

  /**
   * Takes the **DTO**, never the domain object. `JSON.stringify(SavedOverviewView)` would write
   * `viewType`/`'table'` where the server and the still-live v3 expect `view_type`/`'Table'`,
   * orphaning every saved view already in production (contract §5, BE-27-01). The caller passes
   * `serializeSavedView(view)`.
   */
  async upsertOverviewView(
    workspaceId: number | string,
    dto: SavedOverviewViewDto,
    current: OverviewViewRow | null,
  ): Promise<void> {
    await this.http.post(
      '/upsert_table_view_config',
      {
        workspace_id: workspaceId,
        table_id: OVERVIEW_VIEW_TABLE_ID,
        dimensions: current?.dimensions ?? [],
        metrics: current?.metrics ?? [],
        sortField: current?.sortField ?? '',
        sortOrder: current?.sortOrder ?? '',
        breakdownColumn: current?.breakdownColumn ?? '',
        lineMetric: current?.lineMetric ?? '',
        checkLine: current?.checkLine ?? '',
        activeChart: current?.activeChart ?? '',
        groupColumn: current?.groupColumn ?? '',
        mediaColumns: JSON.stringify(dto),
      },
      FEATURE_OWNED_ERRORS,
    )
  }
}
